
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import Layout, {  } from '/vercel/path0/components/layouts/mdx/articles'
export const frontMatter = {
  layout: 'articles',
  date: '2020-08-20',
  title: 'How to use Next.js with Strapi and Apollo',
  category: 'javascript',
  tags: ['react.js', 'next.js', 'cms'],
  description: 'Learn how to use Next.js with Strapi and Apollo.',
  wordCount: 959,
  readingTime: {
    text: '5 min read',
    minutes: 4.785,
    time: 287100,
    words: 957
  },
  __resourcePath: 'articles/2020-08-20-how-to-use-next.js-with-strapi-and-apollo.mdx',
  __scans: {}
};

const layoutProps = {
  frontMatter
};
const MDXLayout = Layout
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <blockquote>
      <p parentName="blockquote">{`In this tutorial, we'll learn how we can use Next.js with Strapi and Apollo.`}</p>
    </blockquote>
    <h2 {...{
      "id": "table-of-contents"
    }}><a parentName="h2" {...{
        "aria-hidden": "true",
        "tabIndex": -1,
        "href": "#table-of-contents"
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Table of Contents`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#introduction"
        }}>{`Introduction`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#creating-a-strapi-application-using-docker"
        }}>{`Creating a Strapi application using Docker`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#creating-a-nextjs-application"
        }}>{`Creating a Next.js application`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#integrating-the-nextjs-application-with-apollo"
        }}>{`Integrating the Next.js application with Apollo`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#conclusion"
        }}>{`Conclusion`}</a></li>
    </ul>
    <h2 {...{
      "id": "introduction"
    }}><a parentName="h2" {...{
        "aria-hidden": "true",
        "tabIndex": -1,
        "href": "#introduction"
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Introduction`}</h2>
    <p>{`In `}<a parentName="p" {...{
        "href": "/articles/2020-01-01-how-to-automate-the-backend-stuffs-with-open-source-headless-cms-strapi-and-docker"
      }}>{`one of my previous articles`}</a>{`, I've written about how to get started using Strapi. In this post, we'll be building a newsfeed application using Next.js. The APIs necessary for the Next.js front-end application will be powered by Strapi. We'll also use `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/"
      }}>{`Apollo`}</a>{` as the GraphQL client.`}</p>
    <p>{`The whole code for the application that we're going to build is available on `}<a parentName="p" {...{
        "href": "https://github.com/ghoshnirmalya/nextjs-strapi-boilerplate"
      }}>{`Github`}</a>{`.`}</p>
    <p>{`Before we proceed, it's better if you have some idea about the following technologies:`}</p>
    <ol>
      <li parentName="ol"><a parentName="li" {...{
          "href": "https://nextjs.org/"
        }}>{`Next.js`}</a></li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "https://www.apollographql.com/"
        }}>{`Apollo`}</a></li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "http://strapi.io/"
        }}>{`Strapi`}</a></li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "https://www.typescriptlang.org/"
        }}>{`TypeScript`}</a></li>
    </ol>
    <p>{`I've been using `}<a parentName="p" {...{
        "href": "http://strapi.io/"
      }}>{`Strapi`}</a>{` for quite some time now and it's very easy to get up and running with it within a very short amount of time. It gives us a lot of `}<a parentName="p" {...{
        "href": "https://strapi.io/features"
      }}>{`features`}</a>{` out of the box:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Single types`}</strong>{`: Create one-off pages that have unique content structure`}</li>
      <li parentName="ol"><strong parentName="li">{`Customizable API`}</strong>{`: With Strapi, you can just hop in your code editor and edit the code to fit your API to your needs.`}</li>
      <li parentName="ol"><strong parentName="li">{`Integrations`}</strong>{`: Strapi supports integrations with Cloudinary, SendGrid, Algolia and others.`}</li>
      <li parentName="ol"><strong parentName="li">{`Editor interface`}</strong>{`: The editor allows you to pull in dynamic blocks of content.`}</li>
      <li parentName="ol"><strong parentName="li">{`Authentication`}</strong>{`: Secure and authorize access to your API with JWT or providers.`}</li>
    </ol>
    <p><a parentName="p" {...{
        "href": "https://nextjs.org/"
      }}>{`Next.js`}</a>{` is a very popular `}<a parentName="p" {...{
        "href": "https://reactjs.org/"
      }}>{`React`}</a>{` framework. It offers a lot of features like:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Zero config`}</strong>{`: Automatic compilation and bundling. Optimized for production from the start.`}</li>
      <li parentName="ol"><strong parentName="li">{`Hybrid: SSG and SSR`}</strong>{`: Pre-render pages at build time (SSG) or request time (SSR) in a single project.`}</li>
      <li parentName="ol"><strong parentName="li">{`Incremental Static Generation`}</strong>{`: Add and update statically pre-rendered pages incrementally after build time.`}</li>
      <li parentName="ol"><strong parentName="li">{`TypeScript Support`}</strong>{`: Automatic TypeScript configuration and compilation.`}</li>
      <li parentName="ol"><strong parentName="li">{`Fast Refresh`}</strong>{`: Automatic TypeScript configuration and compilation.`}</li>
    </ol>
    <p><a parentName="p" {...{
        "href": "https://www.apollographql.com/"
      }}>{`Apollo`}</a>{` is the industry-standard GraphQL implementation, providing the data graph layer that connects modern apps to the cloud. It offers a lot of features like:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Declarative data fetching`}</strong>{`: Write a query and receive data without manually tracking loading, error, or network states.`}</li>
      <li parentName="ol"><strong parentName="li">{`Reactive data cache`}</strong>{`: Cut down on network traffic and keep data consistent throughout your application with Apollo Client’s normalized reactive data cache.`}</li>
      <li parentName="ol"><strong parentName="li">{`Excellent dev experience`}</strong>{`: Enjoy cross stack type safety, runtime cache inspectors, and full featured editor integrations to keep you writing applications faster.`}</li>
      <li parentName="ol"><strong parentName="li">{`Compatible and adoptable`}</strong>{`: Use any build setup and any GraphQL API. Drop Apollo Client into any app seamlessly without re-architecting your entire data strategy.`}</li>
      <li parentName="ol"><strong parentName="li">{`Designed for modern UIs`}</strong>{`: Take advantage of modern UI architectures in the web, iOS, and Android ecosystems.`}</li>
    </ol>
    <p>{`I've created a `}<a parentName="p" {...{
        "href": "https://github.com/ghoshnirmalya/nextjs-strapi-boilerplate"
      }}>{`boilerplate`}</a>{` so that you can get up and running with `}<a parentName="p" {...{
        "href": "http://strapi.io/"
      }}>{`Strapi`}</a>{`, `}<a parentName="p" {...{
        "href": "https://nextjs.org/"
      }}>{`Next.js`}</a>{` and `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/"
      }}>{`Apollo`}</a>{` quickly. Check out the project on `}<a parentName="p" {...{
        "href": "https://github.com/ghoshnirmalya/nextjs-strapi-boilerplate"
      }}>{`Github`}</a>{`.`}</p>
    <a href="https://github.com/ghoshnirmalya/nextjs-strapi-boilerplate" target="_blank">
  <img src="https://user-images.githubusercontent.com/6391763/90599044-ca070300-e211-11ea-8b8a-89354dd30bd5.png" alt="Logo" />
    </a>
    <h2 {...{
      "id": "creating-a-strapi-application-using-docker"
    }}><a parentName="h2" {...{
        "aria-hidden": "true",
        "tabIndex": -1,
        "href": "#creating-a-strapi-application-using-docker"
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Creating a Strapi application using Docker`}</h2>
    <p><strong parentName="p">{`Step 1:`}</strong>{` We need to create a directory named `}<inlineCode parentName="p">{`backend`}</inlineCode>{` and then download the `}<a parentName="p" {...{
        "href": "https://github.com/strapi/strapi-docker/blob/master/examples/postgresql/docker-compose.yml"
      }}>{`docker-compose.yml`}</a>{` file from the `}<a parentName="p" {...{
        "href": "https://github.com/strapi/strapi-docker"
      }}>{`strapi-docker repository`}</a>{`. Now, we need to start Docker. Once Docker is up and running, we can go inside the `}<inlineCode parentName="p">{`backend`}</inlineCode>{` directory and run our Strapi container:`}</p>
    <pre {...{
      "className": "language-bash"
    }}><code parentName="pre" {...{
        "className": "language-bash"
      }}><span parentName="code" {...{
          "className": "token builtin class-name"
        }}>{`cd`}</span>{` backend `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`&&`}</span>{` docker-compose up
`}</code></pre>
    <p>{`This will pull `}<strong parentName="p">{`postgres`}</strong>{` and `}<strong parentName="p">{`strapi/strapi`}</strong>{` images from `}<a parentName="p" {...{
        "href": "https://hub.docker.com/"
      }}>{`Docker Hub`}</a>{`. So, it might take some time for this operation to complete. You can refer to this `}<a parentName="p" {...{
        "href": "https://nirmalyaghosh.com/articles/2020-01-01-how-to-automate-the-backend-stuffs-with-open-source-headless-cms-strapi-and-docker#installing-strapi"
      }}>{`article`}</a>{` for more details regarding how to install Strapi using Docker.`}</p>
    <p><strong parentName="p">{`Step 2:`}</strong>{` We'll have to `}<a parentName="p" {...{
        "href": "https://nirmalyaghosh.com/articles/2020-01-01-how-to-automate-the-backend-stuffs-with-open-source-headless-cms-strapi-and-docker#creating-our-first-administrator-profile"
      }}>{`create our first administrator profile`}</a>{`. Once, our administrator profile is setup, we should be able to log into the admin panel of Strapi.`}</p>
    <p><strong parentName="p">{`Step 3:`}</strong>{` We'll have to `}<a parentName="p" {...{
        "href": "https://nirmalyaghosh.com/articles/2020-01-01-how-to-automate-the-backend-stuffs-with-open-source-headless-cms-strapi-and-docker#adding-a-new-content-type"
      }}>{`add a new content-type`}</a>{`.`}</p>
    <p><strong parentName="p">{`Step 4:`}</strong>{` Install the `}<a parentName="p" {...{
        "href": "https://strapi.io/documentation/3.0.0-beta.x/plugins/graphql.html"
      }}>{`Strapi GraphQL plugin`}</a>{`.`}</p>
    <blockquote>
      <p parentName="blockquote">{`I've already covered most about getting started with Strapi. We're adding links to a previous article in order to keep this tutorial short.`}</p>
    </blockquote>
    <h2 {...{
      "id": "creating-a-nextjs-application"
    }}><a parentName="h2" {...{
        "aria-hidden": "true",
        "tabIndex": -1,
        "href": "#creating-a-nextjs-application"
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Creating a Next.js application`}</h2>
    <p>{`We can create a new Next.js app using create-next-app, which sets up everything automatically for us:`}</p>
    <pre {...{
      "className": "language-bash"
    }}><code parentName="pre" {...{
        "className": "language-bash"
      }}><span parentName="code" {...{
          "className": "token function"
        }}>{`yarn`}</span>{` create next-app
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`The above command will install all the necessary packages as well a create a new directory based on the application name (which you entered during the setup process).`}</p>
    </blockquote>
    <h2 {...{
      "id": "integrating-the-nextjs-application-with-apollo"
    }}><a parentName="h2" {...{
        "aria-hidden": "true",
        "tabIndex": -1,
        "href": "#integrating-the-nextjs-application-with-apollo"
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Integrating the Next.js application with Apollo`}</h2>
    <p>{`In order to integrate Apollo with Next.js, we need to add the required dependencies first:`}</p>
    <pre {...{
      "className": "language-bash"
    }}><code parentName="pre" {...{
        "className": "language-bash"
      }}><span parentName="code" {...{
          "className": "token function"
        }}>{`yarn`}</span>{` `}<span parentName="code" {...{
          "className": "token function"
        }}>{`add`}</span>{`  @apollo/client graphql
`}</code></pre>
    <p>{`Next, we need to create a new file `}<inlineCode parentName="p">{`lib/with-graphql.js`}</inlineCode>{` with the following content:`}</p>
    <div {...{
      "className": "remark-code-title"
    }}>{`lib/with-graphql.js`}</div>
    <pre {...{
      "className": "language-js"
    }}><code parentName="pre" {...{
        "className": "language-js"
      }}><span parentName="code" {...{
          "className": "token keyword module"
        }}>{`import`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`ApolloClient`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`,`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`ApolloProvider`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`,`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`InMemoryCache`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`from`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"@apollo/client"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

`}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`const`}</span>{` `}<span parentName="code" {...{
          "className": "token function-variable function"
        }}><span parentName="span" {...{
            "className": "token maybe-class-name"
          }}>{`WithGraphQL`}</span></span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span><span parentName="code" {...{
          "className": "token parameter"
        }}><span parentName="span" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` children `}<span parentName="span" {...{
            "className": "token punctuation"
          }}>{`}`}</span></span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span>{` `}<span parentName="code" {...{
          "className": "token arrow operator"
        }}>{`=>`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`
  `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`const`}</span>{` client `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`new`}</span>{` `}<span parentName="code" {...{
          "className": "token class-name"
        }}>{`ApolloClient`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`
    uri`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`:`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"http://localhost:1337/graphql"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`,`}</span>{`
    cache`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`:`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`new`}</span>{` `}<span parentName="code" {...{
          "className": "token class-name"
        }}>{`InMemoryCache`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`,`}</span>{`
  `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

  `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`return`}</span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`ApolloProvider`}</span>{` client`}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`client`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`children`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`/`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`ApolloProvider`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

`}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`export`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`default`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`WithGraphQL`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}</code></pre>
    <p>{`Now, we can import this file and wrap any Next.js page where we want to use GraphQL:`}</p>
    <div {...{
      "className": "remark-code-title"
    }}>{`pages/index.js`}</div>
    <pre {...{
      "className": "language-js"
    }}><code parentName="pre" {...{
        "className": "language-js"
      }}><span parentName="code" {...{
          "className": "token keyword module"
        }}>{`import`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`React`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`from`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"react"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`import`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Page`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`from`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"components/pages/index"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`import`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`WithGraphQL`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`from`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"lib/with-graphql"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

`}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`const`}</span>{` `}<span parentName="code" {...{
          "className": "token function-variable function"
        }}><span parentName="span" {...{
            "className": "token maybe-class-name"
          }}>{`IndexPage`}</span></span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span>{` `}<span parentName="code" {...{
          "className": "token arrow operator"
        }}>{`=>`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`
  `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`return`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span>{`
    `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`WithGraphQL`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span>{`
      `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Page`}</span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`/`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span>{`
    `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`/`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`WithGraphQL`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span>{`
  `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

`}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`export`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`default`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`IndexPage`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}</code></pre>
    <p>{`Now, we can use GraphQL queries and mutations in the `}<inlineCode parentName="p">{`components/pages/index.js`}</inlineCode>{` file:`}</p>
    <div {...{
      "className": "remark-code-title"
    }}>{`components/pages/index.js`}</div>
    <pre {...{
      "className": "language-js"
    }}><code parentName="pre" {...{
        "className": "language-js"
      }}><span parentName="code" {...{
          "className": "token keyword module"
        }}>{`import`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{` gql`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`,`}</span>{` useQuery `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`from`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"@apollo/client"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`import`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Box`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`,`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Stack`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`from`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"@chakra-ui/core"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`import`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Feed`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`from`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"components/pages/index/feed"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`import`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`React`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`from`}</span>{` `}<span parentName="code" {...{
          "className": "token string"
        }}>{`"react"`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

`}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`const`}</span>{` feedsQuery `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span>{` gql`}<span parentName="code" {...{
          "className": "token template-string"
        }}><span parentName="span" {...{
            "className": "token template-punctuation string"
          }}>{`\``}</span><span parentName="span" {...{
            "className": "token graphql language-graphql"
          }}>{`
  `}<span parentName="span" {...{
              "className": "token keyword"
            }}>{`query`}</span>{` fetchFeeds `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`{`}</span>{`
    feeds `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`{`}</span>{`
      id
      created_at
      body
      author `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`{`}</span>{`
        id
        username
      `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`}`}</span>{`
    `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`}`}</span>{`
  `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`}`}</span>{`
`}</span><span parentName="span" {...{
            "className": "token template-punctuation string"
          }}>{`\``}</span></span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

`}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`const`}</span>{` `}<span parentName="code" {...{
          "className": "token function-variable function"
        }}><span parentName="span" {...{
            "className": "token maybe-class-name"
          }}>{`FeedsPageComponent`}</span></span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span>{` `}<span parentName="code" {...{
          "className": "token arrow operator"
        }}>{`=>`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`
  `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`const`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{` loading`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`,`}</span>{` error`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`,`}</span>{` data `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span>{` `}<span parentName="code" {...{
          "className": "token function"
        }}>{`useQuery`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span>{`feedsQuery`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

  `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`if`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span>{`loading`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`return`}</span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span>{`p`}<span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Loading`}</span><span parentName="code" {...{
          "className": "token spread operator"
        }}>{`...`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`/`}</span>{`p`}<span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
  `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`if`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span>{`error`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`return`}</span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span>{`p`}<span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span><span parentName="code" {...{
          "className": "token known-class-name class-name"
        }}>{`Error`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`:`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`/`}</span>{`p`}<span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

  `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`return`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span>{`
    `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Stack`}</span>{` spacing`}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span><span parentName="code" {...{
          "className": "token number"
        }}>{`8`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span>{`
      `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`data`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`.`}</span><span parentName="code" {...{
          "className": "token property-access"
        }}>{`feeds`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`.`}</span><span parentName="code" {...{
          "className": "token method function property-access"
        }}>{`map`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span><span parentName="code" {...{
          "className": "token parameter"
        }}>{`feed`}</span>{` `}<span parentName="code" {...{
          "className": "token arrow operator"
        }}>{`=>`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`
        `}<span parentName="code" {...{
          "className": "token keyword"
        }}>{`return`}</span>{` `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`(`}</span>{`
          `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Box`}</span>{` key`}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`feed`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`.`}</span><span parentName="code" {...{
          "className": "token property-access"
        }}>{`id`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span>{`
            `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Feed`}</span>{` feed`}<span parentName="code" {...{
          "className": "token operator"
        }}>{`=`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`{`}</span>{`feed`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span>{` `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`/`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span>{`
          `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`/`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Box`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span>{`
        `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
      `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span>{`
    `}<span parentName="code" {...{
          "className": "token operator"
        }}>{`<`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`/`}</span><span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`Stack`}</span><span parentName="code" {...{
          "className": "token operator"
        }}>{`>`}</span>{`
  `}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`)`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}<span parentName="code" {...{
          "className": "token punctuation"
        }}>{`}`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`

`}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`export`}</span>{` `}<span parentName="code" {...{
          "className": "token keyword module"
        }}>{`default`}</span>{` `}<span parentName="code" {...{
          "className": "token maybe-class-name"
        }}>{`FeedsPageComponent`}</span><span parentName="code" {...{
          "className": "token punctuation"
        }}>{`;`}</span>{`
`}</code></pre>
    <h2 {...{
      "id": "conclusion"
    }}><a parentName="h2" {...{
        "aria-hidden": "true",
        "tabIndex": -1,
        "href": "#conclusion"
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Conclusion`}</h2>
    <p>{`In this tutorial, we've learnt how we can integrate Apollo with Next.js and use it with Strapi. I've created a `}<a parentName="p" {...{
        "href": "https://github.com/ghoshnirmalya/nextjs-strapi-boilerplate"
      }}>{`boilerplate`}</a>{` so that you can get up and running with Strapi, Next.js and Apollo quickly. Check out the project on `}<a parentName="p" {...{
        "href": "https://github.com/ghoshnirmalya/nextjs-strapi-boilerplate"
      }}>{`Github`}</a>{`. Documentation of this project is available `}<a parentName="p" {...{
        "href": "/docs/nextjs-strapi-boilerplate"
      }}>{`here`}</a>{`.`}</p>
    <a href="https://github.com/ghoshnirmalya/nextjs-strapi-boilerplate" target="_blank">
  <img src="https://user-images.githubusercontent.com/6391763/90599044-ca070300-e211-11ea-8b8a-89354dd30bd5.png" alt="Logo" />
    </a>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;